import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  getChannelGraphics,
  deleteChannelCreation,
  channelGraphicsDownloadUrl,
  regenerateChannelGraphics,
} from "@/lib/api";
import { BannerSafeZonePreview } from "@/components/BannerSafeZonePreview";
import RegenerateChannelKitDialog from "@/components/RegenerateChannelKitDialog";
import ChannelBumpersSection from "@/components/ChannelBumpersSection";
import ChannelThumbnailRefsSection from "@/components/ChannelThumbnailRefsSection";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

/**
 * ChannelKitDetail — read-only view of a single saved channel kit
 * (`/create-channel/kit/:jobId`). Reached by clicking a card on the
 * Create Channel gallery.
 *
 * Renders the banner with the YouTube safe-zone overlay, the avatar,
 * name + handle, source-niche provenance, palette (when present), and
 * a primary "↓ Download kit (.zip)" CTA plus a "▸ Continue to
 * Storyboard" jump-off so users can start writing content on the spot.
 *
 * Defensive about missing files — older kits whose on-disk assets
 * were purged during a redeploy fall back to a friendly placeholder
 * and the Download button is hidden so it doesn't 404.
 */
export default function ChannelKitDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bannerBroken, setBannerBroken] = useState(false);
  const [bannerIdx, setBannerIdx] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await getChannelGraphics(jobId);
      setDoc(d);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't load this kit");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  // --- Regenerate with Character Library cast ---
  // Opens a modal that lets the user pick / update the cast pulled
  // from their Character Library, then re-runs banner + avatar
  // generation for THIS kit only (keeps name + handle + niche). The
  // API flips status → running and drops fresh bytes into GridFS,
  // so we optimistically flip UI to a lightweight "regenerating"
  // toast + poll for completion.
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const runRegen = useCallback(async (characterIds, stylePresetKey = null, providerKey = null) => {
    setRegenerating(true);
    try {
      await regenerateChannelGraphics(jobId, characterIds, stylePresetKey, providerKey);
      toast.success("Regenerating — this takes 30–60s.");
      let elapsed = 0;
      const iv = setInterval(async () => {
        elapsed += 4;
        if (elapsed > 180) {
          clearInterval(iv);
          setRegenerating(false);
          load();
          return;
        }
        try {
          const fresh = await getChannelGraphics(jobId);
          if (fresh.status === "complete" || fresh.status === "failed") {
            clearInterval(iv);
            setRegenerating(false);
            setDoc(fresh);
            setBannerBroken(false);
            setBannerIdx(0);
            if (fresh.status === "complete") {
              toast.success("Kit refreshed with your characters");
            } else {
              toast.error(fresh.error || "Regeneration failed");
            }
          }
        } catch { /* keep polling */ }
      }, 4000);
    } catch (err) {
      setRegenerating(false);
      toast.error(err?.response?.data?.detail || "Couldn't start regeneration");
    }
  }, [jobId, load]);

  const removeKit = async () => {
    if (!doc) return;
    const ok = window.confirm(
      `Delete "${doc.name || "this kit"}" from your channels?\n\n` +
      `This permanently removes the saved kit (avatar + banners + name + handle). ` +
      `This cannot be undone.`
    );
    if (!ok) return;
    try {
      await deleteChannelCreation(jobId);
      toast.success(`Removed · ${doc.name || "kit"}`);
      navigate("/create-channel", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete kit");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-zinc-200" data-testid="channel-kit-detail-loading">
        <div className="max-w-5xl mx-auto px-8 py-12 font-mono text-sm text-zinc-500">
          [recon] loading kit<span className="blink">_</span>
        </div>
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="min-h-screen bg-black text-zinc-200" data-testid="channel-kit-detail-error">
        <div className="max-w-5xl mx-auto px-8 py-12">
          <Link
            to="/create-channel"
            className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-yellow-400"
          >
            ← back to your channels
          </Link>
          <div className="mt-10 border border-red-500/40 bg-red-500/10 p-6 rounded-sm">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-red-300 mb-2">
              // KIT NOT FOUND
            </div>
            <p className="text-sm text-zinc-200 leading-relaxed">
              {error || "This kit no longer exists."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const bannerCandidates = [
    doc.images?.["banner_1.png"],
    doc.images?.["banner_2.png"],
    doc.images?.["banner.png"],
  ].filter(Boolean);
  const banner = bannerCandidates[bannerIdx];
  const avatar = doc.images?.["avatar.png"] || doc.images?.["avatar.jpg"];
  const allBanners = bannerCandidates;
  const palette = doc.color_palette || {};
  const paletteKeys = ["primary", "secondary", "accent", "background"].filter(
    (k) => palette[k]
  );

  const isComplete = doc.status === "complete";
  const isFailed = doc.status === "failed";

  return (
    <div className="min-h-screen bg-black text-zinc-200" data-testid="channel-kit-detail-page">
      <div className="max-w-5xl mx-auto px-8 py-12">
        <Link
          to="/create-channel"
          data-testid="ck-back-btn"
          className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-yellow-400 transition-colors"
        >
          ← back to your channels
        </Link>

        {/* Header */}
        <div className="mt-6 mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-2">
              // CHANNEL KIT
            </div>
            <h1
              data-testid="ck-name"
              className="font-display text-4xl sm:text-5xl font-black text-white leading-none break-words"
            >
              {doc.name || "Untitled"}
            </h1>
            {doc.handle && (
              <div
                data-testid="ck-handle"
                className="font-mono text-base text-yellow-400 mt-2"
              >
                {doc.handle}
              </div>
            )}
            <div className="flex items-center gap-3 mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              <span
                data-testid="ck-status"
                className={`px-2 py-0.5 rounded-sm border ${
                  isComplete
                    ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
                    : isFailed
                    ? "border-red-500/40 text-red-300 bg-red-500/5"
                    : "border-yellow-400/40 text-yellow-300 bg-yellow-400/5"
                }`}
              >
                {doc.status?.toUpperCase() || "—"}
              </span>
              <span>{fmtDate(doc.completed_at || doc.created_at)}</span>
              {doc.source_niche && (
                <span
                  data-testid="ck-source"
                  className="px-2 py-0.5 rounded-sm border border-yellow-400/40 text-yellow-300 bg-yellow-400/5"
                  title={`Created from saved niche: ${doc.source_niche}`}
                >
                  FROM {doc.source_niche.toUpperCase()}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {isComplete && !bannerBroken && (
              <a
                data-testid="ck-download-btn"
                href={channelGraphicsDownloadUrl(jobId)}
                className="bg-yellow-400 text-black font-display text-base font-black uppercase tracking-wide px-5 py-2.5 rounded-sm hover:bg-yellow-300 transition-colors shadow-[0_0_0_1px_rgba(250,204,21,0.5)]"
              >
                ↓ Download kit (.zip)
              </a>
            )}
            <button
              type="button"
              data-testid="ck-regen-with-characters-btn"
              onClick={() => setRegenOpen(true)}
              disabled={regenerating}
              title="Re-render the banner + avatar using characters from your Character Library"
              className="font-mono text-xs uppercase tracking-[0.25em] text-yellow-400 border border-yellow-400/60 hover:border-yellow-400 hover:bg-yellow-400/10 px-4 py-2.5 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {regenerating ? "↻ regenerating…" : "↻ use my characters"}
            </button>
            <Link
              data-testid="ck-storyboard-btn"
              to="/storyboards"
              className="font-mono text-xs uppercase tracking-[0.25em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-4 py-2.5 rounded-sm transition-colors"
            >
              ▸ Storyboard
            </Link>
            <button
              type="button"
              data-testid="ck-delete-btn"
              onClick={removeKit}
              title="Delete this kit"
              className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-red-400 border border-zinc-800 hover:border-red-500/60 px-4 py-2.5 rounded-sm transition-colors"
            >
              ✕ Delete
            </button>
          </div>
        </div>

        {regenOpen && (
          <RegenerateChannelKitDialog
            kit={doc}
            onClose={() => setRegenOpen(false)}
            onConfirm={runRegen}
          />
        )}

        {/* Banner preview with YouTube safe-zone overlay */}
        <section className="mb-8" data-testid="ck-banner-section">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">
            01 · Banner · 2560×1440
          </div>
          {banner && !bannerBroken ? (
            <BannerSafeZonePreview
              src={`${API_BASE}${banner}`}
              alt={`${doc.name} banner`}
              onError={() => {
                if (bannerIdx + 1 < bannerCandidates.length) {
                  setBannerIdx(bannerIdx + 1);
                } else {
                  setBannerBroken(true);
                }
              }}
            />
          ) : (
            <div
              data-testid="ck-banner-missing"
              className="border border-dashed border-zinc-800 bg-zinc-950 aspect-[2560/1440] rounded-sm flex flex-col items-center justify-center text-center gap-2 p-6"
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">
                Banner unavailable
              </span>
              <span className="text-xs text-zinc-500 max-w-md">
                The stored asset is missing — likely purged during a pod
                redeploy. Re-create the kit from the wizard to refresh
                visuals while keeping the name + handle.
              </span>
            </div>
          )}
          {allBanners.length > 1 && (
            <div className="flex items-center gap-2 mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              <span>concept:</span>
              {allBanners.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`ck-banner-switch-${i}`}
                  onClick={() => { setBannerIdx(i); setBannerBroken(false); }}
                  className={`px-2 py-0.5 rounded-sm border ${
                    bannerIdx === i
                      ? "border-yellow-400 text-yellow-400"
                      : "border-zinc-800 text-zinc-400 hover:border-yellow-400/60 hover:text-yellow-300"
                  }`}
                >
                  {String.fromCharCode(65 + i)}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Avatar + Palette grid */}
        <section className="grid md:grid-cols-2 gap-6 mb-8">
          <div data-testid="ck-avatar-section">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">
              02 · Avatar · 800×800
            </div>
            {avatar ? (
              <img
                data-testid="ck-avatar-img"
                src={`${API_BASE}${avatar}`}
                alt={`${doc.name} avatar`}
                referrerPolicy="no-referrer"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="w-full max-w-sm aspect-square object-cover rounded-sm border border-zinc-800 bg-zinc-950"
              />
            ) : (
              <div
                data-testid="ck-avatar-missing"
                className="w-full max-w-sm aspect-square border border-dashed border-zinc-800 bg-zinc-950 rounded-sm flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500"
              >
                avatar unavailable
              </div>
            )}
          </div>

          {paletteKeys.length > 0 && (
            <div data-testid="ck-palette-section">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">
                03 · Palette
              </div>
              <div className="grid grid-cols-2 gap-2">
                {paletteKeys.map((k) => (
                  <div
                    key={k}
                    className="border border-zinc-800 rounded-sm bg-black overflow-hidden"
                  >
                    <div
                      className="h-16 w-full"
                      style={{ background: palette[k] }}
                      title={palette[k]}
                    />
                    <div className="px-3 py-2">
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                        {k}
                      </div>
                      <div className="font-mono text-xs text-white">{palette[k]}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Standard intro / outro bumpers */}
        <ChannelBumpersSection jobId={jobId} />

        {/* Reference thumbnail library (aspiration ChatGPT-style refs) */}
        <ChannelThumbnailRefsSection jobId={jobId} />

        {/* Topic / style hint */}
        <section className="mb-10 border border-zinc-900 bg-zinc-950/40 rounded-sm p-5" data-testid="ck-meta">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
            // CHANNEL BRIEF
          </div>
          <Row k="Topic" v={doc.topic} />
          {doc.style_hint && <Row k="Style hint" v={doc.style_hint} />}
          {doc.source_channel_id && (
            <Row k="Inspired by" v={doc.source_channel_id} mono />
          )}
          {isFailed && doc.error && (
            <div className="mt-4 border border-red-500/40 bg-red-500/5 p-3 rounded-sm text-xs text-red-200 leading-relaxed">
              <span className="font-mono uppercase tracking-[0.2em] text-red-300 block mb-1">
                Last generation error
              </span>
              {doc.error}
            </div>
          )}
        </section>

        {/* Next steps */}
        <section className="border-t border-zinc-900 pt-6" data-testid="ck-next-steps">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 mb-3">
            Next steps in YouTube Studio
          </div>
          <ol className="space-y-2 text-sm text-zinc-300 list-decimal list-inside">
            <li>Go to studio.youtube.com → Customization → Branding</li>
            <li>Upload <span className="text-yellow-400">avatar.png</span> as your channel picture</li>
            <li>Upload one of the <span className="text-yellow-400">banner_*.png</span> files as your banner</li>
            {doc.handle && (
              <li>Set your handle to <span className="text-yellow-400">{doc.handle}</span> (or any free handle)</li>
            )}
            <li>Write your About section and hit Publish</li>
            <li>Come back to <Link to="/storyboards" className="text-yellow-400 hover:underline">Storyboard</Link> to start creating content</li>
          </ol>
        </section>
      </div>
    </div>
  );
}

function Row({ k, v, mono }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-4 py-2 border-b border-zinc-900 last:border-b-0 text-sm">
      <span className="font-mono uppercase tracking-[0.2em] text-zinc-500 text-xs pt-1">{k}</span>
      <span className={`text-zinc-100 ${mono ? "font-mono text-xs break-all" : ""}`}>{v || "—"}</span>
    </div>
  );
}
